<script setup lang="ts">
import { onMounted, useTemplateRef } from 'vue';
import type { DashboardComponent } from '../domain/dashboard';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { storeToRefs } from 'pinia';
import { Button } from 'primevue';
import type { elements } from 'chart.js';

const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);

const rootElementRef = useTemplateRef('root');

const component = defineModel<DashboardComponent>('component', {
  required: true,
});

const props = defineProps<{
  makeWidget: (element: HTMLElement) => void;
  deleteWidget: (component: DashboardComponent, element: HTMLElement) => void;
}>();

const emit = defineEmits<{
  delete: [component: DashboardComponent];
  edit: [component: DashboardComponent];
}>();

onMounted(() => {
  const rootElement = rootElementRef.value;
  if (!rootElement) return;
  props.makeWidget(rootElement);
});

function onDelete() {
  const rootElement = rootElementRef.value;
  if (!rootElement) return;
  props.deleteWidget(component.value, rootElement);
}

</script>

<template>
  <div ref="root" class="grid-stack-item" :gs-x="component.x" :gs-y="component.y" :gs-w="component.width"
    :gs-h="component.height" :gs-id="component.id">
    <div class="grid-stack-item-content">
      <div class="header">
        {{ translations.enums.dashboardComponentType(component.type) }}
      </div>
      <div class="content"></div>
      <div class="actions">
        <Button icon="pi pi-pencil" @click="emit('edit', component)"></Button>
        <Button icon="pi pi-trash" severity="danger" @click="onDelete"></Button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.grid-stack-item-content {
  display: grid;
  grid-template-rows: auto 1fr auto;
  background-color: var(--p-surface-200);
  border-radius: 1rem;
  padding-inline: 1.5rem;
  padding-block: 1rem;
}

.p-dark .grid-stack-item-content {
  background-color: var(--p-surface-700);
}

.header {
  grid-row: 1;
}

.content {
  grid-row: 2;
}

.actions {
  grid-row: 3;
  display: flex;
  justify-content: end;
  gap: 0.5rem;
}
</style>
